/**
 * OIDC (Authorization Code + PKCE) para a área /admin, na página de login do
 * Keycloak — é ela que aplica e-mail verificado, required actions e OTP (MFA).
 * Token fica em memória (sem refresh token no localStorage); F5 refaz o
 * round-trip pelo SSO cookie do Keycloak e volta sem pedir credencial.
 */
import Keycloak from 'keycloak-js';
import { setTokenSource } from './tokens';
import { setRefreshHandler } from './refresh';
import { useAuth } from './useAuth';

// Cada app seta VITE_KEYCLOAK_URL (.env.production → auth.dev.favelafood.net);
// ausente/vazio (dev local) cai no default localhost:8180. O replace api.→auth.
// do carve foi aposentado no C5 — cada subdomínio aponta o keycloak explicitamente.
const DEFAULT_AUTH_URL = 'http://localhost:8180';

const resolveUrl = (explicit?: string): string => {
  if (explicit && explicit.length > 0) return explicit;
  const env = import.meta.env.VITE_KEYCLOAK_URL as string | undefined;
  return env && env.length > 0 ? env : DEFAULT_AUTH_URL;
};

export interface KeycloakAuthOptions {
  clientId: string;
  /** Override explícito; default = VITE_KEYCLOAK_URL → localhost:8180. */
  url?: string;
}

let kc: Keycloak | null = null;

/**
 * Configura o singleton OIDC do tab. Idempotente (primeira chamada vence —
 * seguro sob HMR/boot duplicado). Chamar ANTES de qualquer ensureKeycloakLogin().
 */
export function initKeycloakAuth(opts: KeycloakAuthOptions): void {
  if (kc) return; // idempotente; re-init com outro clientId é bug de boot, não suportar
  kc = new Keycloak({
    url: resolveUrl(opts.url),
    realm: 'favelafood',
    clientId: opts.clientId,
  });
}

/** Instância corrente (null antes de initKeycloakAuth). Para testes/diagnóstico. */
export const getKeycloak = (): Keycloak | null => kc;

let initPromise: Promise<boolean> | null = null;
// Carry-forward (review, 2026-07-02): a rejected init resets `initPromise`
// above so the NEXT call retries `.init()` — but a successful retry re-runs
// this whole `.then()` body, including the `setInterval` below. Without this
// guard, a transient failure followed by a successful retry would register a
// SECOND 60s refresh interval that never gets cleared, doubling every future
// `updateToken` call for the rest of the page's life.
let refreshIntervalStarted = false;

/** Garante sessão OIDC (redireciona pro Keycloak se preciso). Idempotente. */
export const ensureKeycloakLogin = (): Promise<boolean> => {
  if (!kc) {
    // Rejeição (não throw síncrono): os call sites — ProtectedRoute/LoginPage —
    // só fazem `.catch()`. Um throw síncrono estouraria dentro do useEffect e
    // derrubaria a árvore (white screen) em vez do ErrorState com retry.
    return Promise.reject(
      new Error('initKeycloakAuth() precisa ser chamado no boot antes do login OIDC'),
    );
  }
  const keycloak = kc; // narrowing estável p/ os closures abaixo
  initPromise ??= keycloak
    .init({ onLoad: 'login-required', pkceMethod: 'S256', checkLoginIframe: false })
    .then((authenticated) => {
      setTokenSource(() => keycloak.token ?? null);
      // Handler de refresh OIDC — espelha o setTokenSource acima. Plugado só aqui
      // (pós-init); o transporte password-grant que ele substituía morreu no C5.
      setRefreshHandler(async () => {
        try {
          await keycloak.updateToken(-1); // -1 = força refresh; o servidor acabou de dizer 401
          return true;
        } catch {
          return false;
        }
      });
      useAuth.getState().refreshLoginState();
      // Provisiona a linha do usuário no backend (KC→DB) logo após o login. Sem
      // isto, quem foi criado direto no Keycloak (admin do bootstrap, login Google
      // via browser flow) nunca ganha row em `users` e some do painel admin.
      // GET /users/me faz find-or-create no backend (deriva a role do token).
      // Fire-and-forget: falha não bloqueia o login; o próximo boot re-tenta.
      // import() dinâmico evita ciclo estático (client.ts já importa deste módulo).
      if (authenticated) {
        void import('../api/client')
          .then(({ api }) => api.GET('/api/v1/users/me' as never, {} as never))
          .catch(() => console.warn('[auth] falha ao provisionar /users/me; re-tenta no próximo login'));
      }
      // keycloak-js não renova sozinho: renova quando faltar <70s de validade.
      if (!refreshIntervalStarted) {
        refreshIntervalStarted = true;
        window.setInterval(() => {
          void refreshTokenTick(keycloak);
        }, 60_000);
      }
      return authenticated;
    })
    // Hardening item A (review): sem isso, um init rejeitado (ex: Keycloak
    // fora do ar num request pontual) latcha PARA SEMPRE — toda chamada
    // seguinte reawaita a mesma promise morta até um F5. Resetar antes de
    // repropagar deixa a PRÓXIMA chamada tentar de novo.
    .catch((error: unknown) => {
      initPromise = null;
      throw error;
    });
  return initPromise;
};

/** Subconjunto do adapter `Keycloak` usado por {@link refreshTokenTick} — reduz o mock em teste ao essencial. */
interface RefreshableKeycloak {
  updateToken(minValidity: number): Promise<boolean>;
  isTokenExpired(): boolean;
  login(): Promise<void> | void;
}

/**
 * Um tick do intervalo de renovação (hardening item B, review): uma falha de
 * `updateToken` NÃO redireciona pro login em qualquer erro (uma instabilidade
 * de rede durante um deploy faria o operador perder o formulário em edição).
 * Só força login se a sessão está genuinamente morta (`isTokenExpired()`);
 * qualquer outra falha só loga um aviso e deixa o próximo tick tentar de novo.
 */
export const refreshTokenTick = async (kc: RefreshableKeycloak): Promise<void> => {
  try {
    await kc.updateToken(70);
  } catch {
    if (kc.isTokenExpired()) {
      void kc.login();
    } else {
      console.warn('[auth] falha ao renovar o token OIDC; nova tentativa no próximo tick');
    }
  }
};

export const isOidcSession = (): boolean => kc?.authenticated === true;

/**
 * Sessão morta (401 após refresh falho): manda pro login. OIDC → `kc.login()`
 * (redirect pro Keycloak). Sem instância KC → raiz (`/`), onde o PanelGuard
 * assume e re-dirige o login. Usado pelos fetchers no branch `isOidcSession()`.
 */
export const forceLogin = (): void => {
  if (kc) {
    void kc.login();
    return;
  }
  if (typeof window !== 'undefined') window.location.assign('/');
};

// Assunção: `keycloak.logout()` sempre faz um full-navigate (redireciona o
// browser pro Keycloak e volta) — nunca resolve "in place". Se um logout
// SPA-only (sem navegação) aparecer depois, reveja isto: `initPromise` e o
// `tokenSource` (setado em `ensureKeycloakLogin`) precisam ser resetados
// aqui também, senão a próxima sessão herda o estado da anterior.
export const keycloakLogout = (redirectUri?: string): void => {
  if (!kc) return;
  void kc.logout({
    // ponytail: post-logout lands on the panel root (`/`); each subdomain's
    // PanelGuard re-drives login. Legacy `/admin/login` default retired in the
    // 3-app carve — realm post-logout URIs must include `origin/` (dev-realm.json).
    redirectUri: redirectUri ?? `${window.location.origin}/`,
  });
};
